import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    } else {
      setIsDark(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleTheme = () => {
    setIsDark(prev => {
      const newTheme = !prev;
      if (newTheme) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
      }
      return newTheme;
    });
  };

  const theme = {
    isDark,
    toggleTheme,
    colors: {
      // Light theme colors
      light: {
        bg: '#F2F2F2',
        cardBg: '#FFFFFF',
        text: '#232323',
        textSecondary: 'rgba(35, 35, 35, 0.7)',
        border: '#232323',
        borderLight: 'rgba(35, 35, 35, 0.1)',
        accent: '#38FF62',
        accentHover: '#2AE052',
        gridBg: '#F2F2F2'
      },
      // Dark theme colors
      dark: {
        bg: '#0f0f10',
        cardBg: '#1a1a1b',
        text: '#FFFFFF',
        textSecondary: 'rgba(255, 255, 255, 0.7)',
        border: '#FFFFFF',
        borderLight: 'rgba(255, 255, 255, 0.1)',
        accent: '#38FF62',
        accentHover: '#2AE052',
        gridBg: '#0f0f10'
      }
    }
  };

  const currentColors = isDark ? theme.colors.dark : theme.colors.light;

  return (
    <ThemeContext.Provider value={{ ...theme, currentColors }}>
      {children}
    </ThemeContext.Provider>
  );
};